import { Dialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import React, { FC, Fragment, Ref, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePrint } from '../hooks';
import { ArrowLeftIcon, CloseIcon, EditGrayIcon, PrintGrayIcon, TrashGrayIcon } from '../icons';

export interface ModalContentWrapperProps {
  open: boolean;
  children: React.ReactNode;
  title: string | React.ReactNode | JSX.Element;
  headerAction?: React.ReactNode;
  variant?: 'center' | 'right';
  onClose: () => void;
  onBack?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  showClose?: boolean;
  modalScrollRef?: Ref<HTMLDivElement>;
  scrollableTarget?: string;
  classes?: {
    titleWrapper?: string;
    title?: string;
    dialog?: string;
    dialogPanel?: string;
    children?: string;
  };
  showPrint?: boolean;
  persistOnNavigate?: boolean;
}

export const ModalContentWrapper: FC<ModalContentWrapperProps> = ({
  open,
  title,
  headerAction,
  onBack,
  onClose,
  classes,
  children,
  onDelete,
  onEdit,
  modalScrollRef,
  scrollableTarget,
  variant = 'center',
  showClose = true,
  showPrint = false,
  persistOnNavigate = false,
}) => {
  const location = useLocation();
  const { print } = usePrint();
  const currentPathName = useRef(location.pathname);

  useEffect(() => {
    if (persistOnNavigate) return;
    if (location.pathname !== currentPathName.current) {
      onClose();
      currentPathName.current = location.pathname;
    }
  }, [currentPathName, location.pathname, onClose, persistOnNavigate]);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog className={classNames('relative z-50', classes?.dialog)} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div
          className={classNames('fixed inset-0 overflow-y-auto', {
            'right-0': variant === 'right',
          })}
          id={scrollableTarget || 'modal_scroll'}
          ref={modalScrollRef}
        >
          <div
            className={classNames('flex min-h-full text-center', {
              'justify-center items-center p-4': variant === 'center',
              'justify-end': variant === 'right',
            })}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  'w-full transform bg-white text-left align-middle shadow-xl transition-all break-words',
                  {
                    'rounded-xl max-w-xl': variant === 'center',
                    'grid grid-rows-[auto_1fr] max-w-[475px]': variant === 'right',
                  },
                  classes?.dialogPanel,
                )}
                style={{ wordBreak: 'break-word' }}
              >
                <Dialog.Title
                  as="div"
                  className="w-full flex items-start justify-between px-3 sm:px-6 pt-5 min-w-0"
                >
                  <div className={classNames('flex gap-3 items-center', classes?.titleWrapper)}>
                    {onBack && (
                      <button
                        onClick={onBack}
                        className="flex-none hover:opacity-80 hover:cursor-pointer"
                      >
                        <img src={ArrowLeftIcon} alt="back" className="w-6 h-6 min-w-6 min-h-6" />
                      </button>
                    )}
                    <span
                      className={classNames(
                        'text-[24px] leading-[32px] break-words',
                        {
                          'font-medium font-serif': !classes?.title?.includes('font-'),
                        },
                        classes?.title,
                      )}
                      style={{ wordBreak: 'break-word' }}
                    >
                      {title}
                    </span>
                  </div>
                  <div className="flex gap-3 items-center select-none">
                    {headerAction}
                    {showPrint && (
                      <span onClick={print} className="hover:opacity-80 cursor-pointer">
                        <img src={PrintGrayIcon} alt="Print" className="w-6 h-6 min-w-6 min-h-6" />
                      </span>
                    )}
                    {onEdit && (
                      <span onClick={onEdit} className="hover:opacity-80 cursor-pointer">
                        <img src={EditGrayIcon} alt="Edit" className="w-6 h-6 min-w-6 min-h-6" />
                      </span>
                    )}
                    {onDelete && (
                      <span onClick={onDelete} className="hover:opacity-80 cursor-pointer">
                        <img src={TrashGrayIcon} alt="Delete" className="w-6 h-6 min-w-6 min-h-6" />
                      </span>
                    )}
                    {showClose === true && (
                      <span onClick={onClose} className="hover:opacity-80 cursor-pointer">
                        <img src={CloseIcon} alt="Close" className="w-6 h-6 min-w-6 min-h-6" />
                      </span>
                    )}
                  </div>
                </Dialog.Title>
                <div
                  className={classNames(
                    {
                      'pb-5': !classes?.children?.includes('pb-'),
                      'p-3 sm:p-6': !classes?.children?.includes('p-'),
                    },
                    classes?.children,
                  )}
                >
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
